import React, { useEffect, useMemo, useState } from 'react'
import { KTIcon } from '../../_metronic/helpers'
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { formatDateWithDateAndMonthName, getValueFromResource } from '../../common/timeLiveFunction';
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender, ColumnDef, SortingState, ColumnOrderState, ColumnSizingState } from '@tanstack/react-table'
import FirstChechin from '../../assets/image/FirstCheckin.svg';
import LastCheckout from "../../assets/image/LastCheckout.svg";
import WorkHours from "../../assets/image/WorkedHours.svg";
import BreakHours from "../../assets/image/BreakHours.svg";
import Overtime from "../../assets/image/Ovettime.svg";
import AttendanceStatus from "../../assets/image/AttendanceStatus.svg";
import AttendanceSource from "../../assets/image/AttendanceSource.svg";
import './Geofencemodal.css'
import GeofenceModal from './GeofenceModal';
import Loader from '../../common/Loader';
import moment from 'moment';
import { format12HoursOnly } from '../../common/UiUtilities';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { GetAttendanceGeofenceLocationActivity } from './AttendanceSlice';

const formatHoursMinutes = (time: any) => {
    if (!time) return '0hr 0m'
    const parts = time.split(':')
    const hours = parseInt(parts[0], 10) || 0
    const minutes = parseInt(parts[1], 10) || 0
    return `${hours}hr ${minutes}m`
}

const TOOLTIP_STYLE = {
    fontSize: 12, fontWeight: 'normal', padding: 8, borderRadius: 6,
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
    backgroundColor: 'black', color: 'white', zIndex: 999999,
    maxWidth: 220, whiteSpace: 'normal' as const,
};

const STATUS_STYLE_MAP: Record<string, { bg: string; color: string }> = {
    present: { bg: '#EAF3DE', color: '#3B6D11' },
    clockedin: {bg: '#EAF3DE', color: '#3B6D11'},
    clockedout: { bg: '#FCEBEB', color: '#A32D2D' },
    onbreak: { bg: '#FCEBEB', color: '#A32D2D' },
    absent: { bg: '#FCEBEB', color: '#A32D2D' },
    'short day': { bg: '#FAEEDA', color: '#854F0B' },
    'half day': { bg: '#E6F8F4', color: '#3CBA9F' },
    'non working day': { bg: '#E6F1FB', color: '#185FA5' },
    'custom': { bg: '#F1EFE8', color: '#5F5E5A' },
    partial_day: { bg: '#FAEEDA', color: '#854F0B' },
    weekend: { bg: '#F1EFE8', color: '#5F5E5A' },
    holiday: { bg: '#E6F1FB', color: '#185FA5' },
}

const AttendanceDetail = (props: any) => {
    const { onClose } = props;
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const { AccountEmployeeAttendanceDetails, isLoading } = useAppSelector((state) => state.attendance);
    const { decimalPlaces, cultureInfoName } = useAppSelector((state) => state.globalState);
    const [sorting, setSorting] = useState<SortingState>([])
    const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})
    const [showGeofenceModal, setShowGeofenceModal] = useState(false);
    const [selectedActivityKey, setSelectedActivityKey] = useState<any>(null);
    let Summary = AccountEmployeeAttendanceDetails[0]?.Summary;
    const Activity = useMemo(
        () => AccountEmployeeAttendanceDetails[0]?.Activities ?? [],
        [AccountEmployeeAttendanceDetails]
    )
    const STAT_CARDS = [
        { icon: FirstChechin, val: Summary?.FirstCheckIn ? format12HoursOnly(Summary?.FirstCheckIn, 0) : '—', lbl: 'Clock-In' },
        { icon: LastCheckout, val: Summary?.LastCheckOut ? format12HoursOnly(Summary?.LastCheckOut, 0) : '—', lbl: 'Clock-Out' },
        { icon: WorkHours, val: formatHoursMinutes(Summary?.WorkedHours), lbl: 'Worked Hours' },
        { icon: BreakHours, val: formatHoursMinutes(Summary?.BreakHours), lbl: 'Break Hours' },
        // { icon: Overtime, val: formatHoursMinutes(Summary?.OvertimeHours), lbl: 'Overtime' },
        { icon: AttendanceStatus, val: Summary?.AttendanceStatus ?? '—', lbl: 'Attendance Status' },
        { icon: AttendanceSource, val: Summary?.AttendanceSource ?? '—', lbl: 'Attendance Source' },
    ]

    const columns = useMemo(() => [
        {
            id: 'time',
            accessorKey: 'EventTime',
            header: () => getValueFromResource('Time', dispatch, t),
            cell: (info: any) => <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{format12HoursOnly(info.getValue(), 0)}</span>,
            // cell: ({ row }: any) => <span>{format12HoursOnly(row?.origional?.EventTime, 0)}</span>,
            enableSorting: true,
        },
        {
            id: 'activitytype',
            accessorKey: 'ActivityType',
            header: () => getValueFromResource('Activity Type', dispatch, t),
            cell: ({ row }: any) => {
                const value = row?.original?.ActivityType
                const config: any = {
                    'Clock In': { color: '#50CD89' },
                    'Clock Out': { color: '#50CD89' },
                    'Break In': { color: '#3699FF' },
                    'Break Out': { color: '#F59E0B' },
                    'Overtime Start': { color: '#8B5CF6' },
                    'Overtime End': { color: '#8B5CF6' },
                    'ClockedIn': {color: '#3B6D11'},
                    'ClockedOut': {color: '#A32D2D'},
                     'On Break': {color: '#3B6D11'},
                    'Non Working Day': {color: '#185FA5'},
                    'Custom': {color: '#5F5E5A'}
                }

                return (
                    <div className="d-flex align-items-center gap-3">
                        <span
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: config[value]?.color || '#ADB5BD',
                                display: 'inline-block',
                            }}
                        />
                        <span>{value}</span>
                    </div>
                )
            },
            enableSorting: true,
        },
        {
            id: 'source',
            accessorKey: 'Source',
            header: () => getValueFromResource('source', dispatch, t),
            cell: ({ row }: any) => <span>{row?.original?.Source}</span>,
            enableSorting: true,
        },
        {
            id: 'device',
            accessorKey: 'Device',
            header: () => getValueFromResource('Device', dispatch, t),
            cell: ({ row }: any) => <span>{row?.original?.Device}</span>,
            enableSorting: true,
        },
        {
            id: 'status',
            accessorKey: 'GeofenceStatus',
            header: () => getValueFromResource('Geofence Status', dispatch, t),
            cell: ({ row }: any) => {
                if (row?.original?.GeofenceStatus == null || row?.original?.GeofenceStatus == undefined) {
                    return <span>—</span>
                }
                const value = row?.original?.GeofenceStatus ?? '—'
                const isInside = value === 'Inside'
                const data = row?.original?.GeofenceStatus + ' Geofence'

                return (
                    <span
                        style={{
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: 600,
                            background: isInside ? '#E8FFF3' : '#FFF5F8',
                            color: isInside ? '#50CD89' : '#F1416C',
                            display: 'inline-flex',
                            alignItems: 'center',
                        }}
                    >
                        {row?.original?.GeofenceStatus ? ` ${data}` : value}
                    </span>
                )
            },
            enableSorting: true,
        },
        {
            id: 'notes',
            accessorKey: 'Notes',
            header: () => getValueFromResource('Notes', dispatch, t),
            cell: ({ row }: any) => <span>{row?.original?.Notes ?? '—'}</span>,
            enableSorting: true,
        }
    ], [dispatch, t])

    const table = useReactTable({
        data: Activity,
        columns,
        state: { sorting, columnSizing },
        onColumnSizingChange: setColumnSizing,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        columnResizeMode: 'onChange',
    })

    const SortableHeader = ({ header, title }: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>{title}</span>
            {header.column.getCanSort() && (
                <span
                    onClick={header.column.getToggleSortingHandler()}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        cursor: 'pointer',
                        marginLeft: 2,
                    }}
                >
                    <span
                        style={{
                            width: 0,
                            height: 0,
                            borderLeft: '3.5px solid transparent',
                            borderRight: '3.5px solid transparent',
                            borderBottom: `4px solid ${header.column.getIsSorted() === 'asc' ? '#4361EE' : '#C4C4C4'}`,
                        }}
                    />
                    <span
                        style={{
                            width: 0,
                            height: 0,
                            borderLeft: '3.5px solid transparent',
                            borderRight: '3.5px solid transparent',
                            borderTop: `4px solid ${header.column.getIsSorted() === 'desc' ? '#4361EE' : '#C4C4C4'}`,
                        }}
                    />
                </span>
            )}
        </div>
    )

    const onCloseModal = () => {
        setShowGeofenceModal(false);
    }
    return (
        <div>
            {isLoading && <Loader />}
            <div className="modal drawer-overlay" tabIndex={-1} id="attendanceModal" data-bs-backdrop="false" role="dialog" aria-labelledby="exampleModalSizeSm" aria-hidden="true"
                style={{ zIndex: '10002', display: 'block', }}
            >
                <div className="modal-dialog"
                    style={{
                        maxWidth: '90vw',
                        width: '90vw',
                        marginTop: '10px',
                    }}
                >
                    <div className="modal-content" style={{ borderRadius: '8px' }}>
                        <div className="modal-header py-4 px-5 border-bottom">
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <div className="d-flex align-items-center gap-5">
                                    <div className="symbol symbol-60px">
                                        <img
                                            src="/media/avatars/300-1.jpg"
                                            alt="employee"
                                            className="rounded-circle"
                                        />
                                    </div>

                                    <div className="d-flex align-items-center flex-wrap gap-4">
                                        <div className="detail-item">
                                            <div className="gf-value">{Summary?.EmployeeName}</div>
                                            <div className="text-muted fs-7">{Summary?.EmployeeCode}</div>
                                        </div>

                                        <div className="detail-item">
                                            <div className="text-muted fs-8">Department</div>
                                            <div className="gf-header-val">{Summary?.DepartmentName?.length > 20 ? `${Summary.DepartmentName.substring(0, 20)}...` : Summary?.DepartmentName}</div>
                                        </div>

                                        {/* <div className="detail-item">
                                            <div className="text-muted fs-8">Team</div>
                                            <div className="gf-header-val">Field Services Team</div>
                                        </div> */}

                                        <div className="detail-item">
                                            <div className="text-muted fs-8">Shift</div>
                                            <div className="gf-header-val">{Summary?.ShiftName?.length > 20 ? `${Summary.ShiftName.substring(0, 20)}...` : Summary?.ShiftName}</div>
                                        </div>

                                        <div className="detail-item no-border">
                                            <div className="text-muted fs-8">Attendance Date</div>
                                            <div className="gf-header-val">
                                                {Summary?.AttendanceDate ? formatDateWithDateAndMonthName(Summary?.AttendanceDate, cultureInfoName.toString()) : '—'}
                                                <KTIcon iconName="calendar" className="fs-6 ms-2" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="d-flex align-items-center gap-3">
                                    <div
                                        className="btn btn-sm btn-icon btn-active-light-primary"
                                        onClick={onClose}
                                    >
                                        <KTIcon iconName="cross" className="fs-2" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='modal-body pb-4 px-4 pt-4' style={{ overflowY: 'auto', maxHeight: 'calc(99vh - 200px)' }}>
                            <div className="attendance-summary mb-4">
                                {STAT_CARDS.map((card, index) => (
                                    <div className="attendance-card" key={index}>
                                        <div className="attendance-icon">
                                            <img src={card.icon} alt={card.lbl} />
                                        </div>
                                        <div>
                                            <div className="attendance-label">{card.lbl}</div>
                                            {card.lbl === 'Attendance Status' ? (
                                                <span
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        background: STATUS_STYLE_MAP[(Summary?.AttendanceStatus ?? '').toLowerCase()]?.bg || '#F1EFE8',
                                                        color: STATUS_STYLE_MAP[(Summary?.AttendanceStatus ?? '').toLowerCase()]?.color || '#5F5E5A',
                                                        borderRadius: '6px',
                                                        padding: '2px 5px',
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        whiteSpace: 'nowrap',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    {card.val}
                                                </span>
                                            ) : card.lbl === 'Attendance Source' ? (
                                                <div className="attendance-value-sm">{card.val}</div>
                                            ) : (
                                                <div className="attendance-value">{card.val}</div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mb-5">
                                <span className="attendance-value">Attendance Activity</span>
                                <p className='attendance-label'>Each row represent a single attendance activity.</p>
                                <div className="table-responsive mt-5">
                                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                                        <thead style={{ background: '#F8F9FA' }}>
                                            {table?.getHeaderGroups().map((headerGroup) => (
                                                <tr key={headerGroup.id}>
                                                    {headerGroup.headers.map((header) => (
                                                        <th
                                                            key={header.id}
                                                            style={{
                                                                padding: '10px 12px',
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                color: '#343A40',
                                                                borderBottom: '1px solid #E9ECEF',
                                                                whiteSpace: 'nowrap',
                                                                background: '#F8F9FA',
                                                                opacity: 1
                                                            }}
                                                        >
                                                            <SortableHeader
                                                                header={header}
                                                                title={flexRender(header.column.columnDef.header, header.getContext())}
                                                            />
                                                            {/* {flexRender(header.column.columnDef.header, header.getContext())} */}
                                                        </th>
                                                    ))}
                                                    <th style={{ padding: '10px 12px', fontSize: 12, fontWeight: 600, color: '#343A40', borderBottom: '1px solid #E9ECEF', background: '#F8F9FA' }}>
                                                        {getValueFromResource('Actions', dispatch, t)}
                                                    </th>
                                                </tr>
                                            ))}
                                        </thead>

                                        <tbody>
                                            {table?.getRowModel().rows.map((row) => (
                                                <tr
                                                    key={row.id}
                                                    style={{ background: '#fff', borderBottom: '1px solid #E9ECEF' }}
                                                    onMouseEnter={e => (e.currentTarget.style.background = '#F5F7FF')}
                                                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                                >
                                                    {row.getVisibleCells().map((cell) => (
                                                        <td key={cell.id} style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                        </td>
                                                    ))}
                                                    <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                                                        <div style={{ display: 'flex', gap: 8 }}>
                                                            <button onClick={() => {
                                                                const original: any = row?.original;
                                                                setSelectedActivityKey(`${original?.AccountEmployeeAttendanceId}_${original?.EventTime}`);
                                                                const payload = {
                                                                    AccountEmployeeId: row?.original?.AccountEmployeeId,
                                                                    AttendanceDate: moment(Summary?.AttendanceDate).format('YYYY-MM-DD')
                                                                }
                                                                dispatch(GetAttendanceGeofenceLocationActivity(payload));
                                                                setShowGeofenceModal(true)
                                                            }}
                                                                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, color: '#ADB5BD', borderRadius: 4 }} title="Location">
                                                                <i
                                                                    className="ki-duotone ki-geolocation fs-5"
                                                                    data-tooltip-id="tooltip-description"
                                                                    data-tooltip-content={getValueFromResource('Location', dispatch, t)}
                                                                >
                                                                    <span className="path1" /><span className="path2" />
                                                                </i>
                                                            </button>
                                                            {/* <button style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, color: '#ADB5BD', borderRadius: 4 }} title="More">
                                                                <i className='bi bi-three-dots-vertical fs-5'></i>
                                                            </button> */}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            {table.getRowModel().rows.length === 0 && (
                                                <tr>
                                                    <td colSpan={columns.length} className='text-center text-muted py-4' style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                                                        <p className='noDataFound'>
                                                            {getValueFromResource('No Record Found', dispatch, t)}
                                                        </p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div className='modal-footer py-3 px-5 border-top bg-white'>
                            <button onClick={onClose} type='button' className='btn btn-secondary btn-sm me-2' id='kt_OffCanvas'>
                                {getValueFromResource('Close', dispatch, t)}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {showGeofenceModal && <GeofenceModal onCloseModal={onCloseModal} showGeofenceModal={showGeofenceModal} initialActivityKey={selectedActivityKey} />}
            <ReactTooltip id="tooltip-description" style={TOOLTIP_STYLE} place="top" delayShow={100} delayHide={100} clickable />
        </div>
    )
}

export default AttendanceDetail